/**
 * `%` starts a COMMENT in KaTeX, and nothing was escaping it.
 *
 * ── WHAT THIS FIXES, MEASURED ─────────────────────────────────────────────────────────────────
 * Against katex 0.16.45, `50%` renders as "50" and `x = 50% + y` renders as "x = 50". The rest of
 * the source after the `%` is swallowed as a comment. The teacher sees no error, because KaTeX
 * reports this as a strict-mode WARNING (`commentAtEnd`) and not as a throw — so `throwOnError`,
 * which both render paths set, never fires. The truncated form is what gets stored and shown.
 *
 * ── WHY ESCAPING AND NOT A PALETTE BUTTON ─────────────────────────────────────────────────────
 * `%` is on every keyboard, so a teacher writing a percent yield or an error margin types it. The
 * obvious action was the one that destroyed the equation. A button would not have helped the
 * teachers who type it anyway, and there was nothing to warn them with.
 *
 * ── WHY COUNTING BACKSLASHES, AND NOT `/[^\\]%/` ──────────────────────────────────────────────
 * A `%` is already escaped iff it is preceded by an ODD number of backslashes. The naive
 * "not preceded by a backslash" test gets `\\%` wrong — that is an escaped BACKSLASH followed by
 * a live comment character, so it still needs escaping, but the naive test skips it. Counting is
 * the only way to tell `\%` (escaped percent) from `\\%` (escaped backslash, then a comment).
 */

/**
 * Escape every unescaped `%` in a LaTeX source string, leaving `\%` alone.
 *
 * Applied at render time rather than on input so that content stored before this existed is fixed
 * too, and so the stored source stays what the teacher actually typed.
 */
export function escapeUnescapedPercent(src: string): string {
  if (!src.includes('%')) return src;

  let out = '';
  let backslashRun = 0;

  for (const ch of src) {
    if (ch === '%' && backslashRun % 2 === 0) out += '\\%';
    else out += ch;

    backslashRun = ch === '\\' ? backslashRun + 1 : 0;
  }

  return out;
}
